import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import { Loader, Tiles, ContainerControlSite, Content, Text } from '@simplybenefits/sb-ui-library';
import { uploadFile } from '../../actions/master.js';
import Header from '../../components/Header.js';
import FileDropZoneModal from '../../components/FileDropZoneModal.js';
import permissionLevel from '../../services/permissions.js';

class Uploads extends Component {
    state = {
        uploadType: null,
        modalOpen: false,
        title: '',
        instructions: '',
        warning: '',
        failedRecords: null
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Data Uploads', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    _toggleModal = (modalAction = 'close', uploadType = false) => {
        if (modalAction === 'open') {
            let instructions = '';
            let warning = '';

            switch (uploadType) {
                case 'dentalFees':
                    instructions = 'Required Columns Are "code", "codeType", "enDesc", "frDesc", "unitsOfScaling", "claimTypeID", "year", "feeBC", "feeAB", "feeMB", "feeSK", "feeON", "feeNB", "feeNL", "feeNS", "feePE", "feeNT", "feeNU", "feeQC", "feeYK" and "isFlagged".';
                    break;
                case 'optionalBenefits':
                    instructions = 'Required Columns Are "email", "product", "carrier", "volume", "rate", "rateType (gross or net)", "markup", "commissionPercentage", "isGrandfathered", "birthdate (YYYY-MM-DD) - optional".';
                    break;
                case 'miscPremiums':
                    instructions = 'Required Columns Are "email", "type", "volume", "netRate", "markup" and "commissionPercentage".';
                    break;
                case 'adjustments':
                    instructions = 'Required Columns are "email", "benefit", "net", "PST", "RST", "GST", "HST", "total" (can be negative (ex: -100.01)) and “isProtect” (Yes or No) with an optional field of “note”. If not protected, “referencingInvoiceNum” is required. If protected “referencingInvoiceNum” is optional but recommended.';
                    warning = 'Manual adjustments should be calculated per month. Currency fields may have up to 3 decimals. The allowed benefit values are: “life”, “add”, “depLife”, “std”, “ltd”, “ci”, “ciSpouse”, “ciChild”, “health”, “stopLoss”, “outOfCountry”, “efap”, “wello”, “virtualHealthcare”, “dental”, “hsa”, “lsa”, “miscPremiums”, “optionalBenefits”. Adjustments to Member invoices should use the parent invoice number for the “referencingInvoiceNum”.';
                    break;
                case 'dentalPayees':
                    instructions = 'Required Columns are "Office Num", "Address", "Province", "Phone", "Postal Code", "First Name", "Last Name", "CDA ID", "CDANet UIN"';
                    warning = 'These values should only be taken from the Instream provided Payee sheet.';
                    break;
                case 'grandfatherClaims':
                    instructions = 'Required Columns are "First Name", "Last Name", "Birthdate (YYYY-MM-DD)", "Relationship", "Benefit", "Service", "Amount Submitted", "Adjudicated Amount", "Amount Paid", "Service Date (YYYY-MM-DD)", "Date Paid (YYYY-MM-DD)", "Carrier", "Province", "Deductible Paid", "Procedure Number (Applicable for Dental only)", "ASO"';
                    break;
                case 'claims':
                    instructions = 'Required Columns are "cert", "category", "service", "status (Paid or Declined)", "amountSubmitted", "adjudicatedAmount", "amountPaid", "deductiblePaid", "carrier", "serviceDate (YYYY-MM-DD)"';
                    break;
                default:
                    instructions = '';
            }

            this.setState({
                uploadType,
                instructions,
                warning,
                modalOpen: true
            });
        } else {
            this.setState({
                modalOpen: false,
                title: '',
                instructions: '',
                warning: ''
            });
        }
    };

    _submitFile = async (files, uploadType) => {
        this.setState({ failedRecords: null });

        const form = new FormData();
        await form.append('file', files[0]);

        const failedRecords = await this.props.dispatch(uploadFile(form, uploadType));

        if (failedRecords) {
            this.setState({
                failedRecords,
                modalOpen: false
            });
        }
    };

    _downloadTemplate = () => {
        let exampleData = false;
        switch (this.state.uploadType) {
            case 'dentalFees':
                exampleData = {
                    code: '1231234233',
                    codeType: 'General (GP)',
                    enDesc: 'English Description',
                    frDesc: 'French Description',
                    claimTypeID: '3',
                    year: '2020',
                    unitsOfScaling: 'Leave empty for no scaling/planing',
                    feeBC: 1,
                    feeAB: 1,
                    feeMB: 1,
                    feeSK: 1,
                    feeON: 1,
                    feeNB: 1,
                    feeNL: 1,
                    feeNS: 1,
                    feePE: 1,
                    feeNT: 1,
                    feeNU: 1,
                    feeQC: 1,
                    feeYK: 1,
                    isFlagged: 0
                };

                break;
            case 'optionalBenefits':
                exampleData = {
                    email: 'dude@simply.ca',
                    product: 'Critical Illness, Critical Illness (Spouse), Life Insurance, Life Insurance (Spouse), Accidental Death and Dismemberment, Accidental Death and Dismemberment (Spouse)',
                    carrier: 'Allstate',
                    volume: '35000',
                    rate: '0.1',
                    rateType: 'net',
                    markup: '13',
                    commissionPercentage: '5',
                    isGrandfathered: 1,
                    birthdate: "2024-01-30"
                };

                break;
            case 'miscPremiums':
                exampleData = {
                    email: 'dude@simply.ca',
                    type: 'MyCareAdvantage, HBO',
                    volume: '35000',
                    netRate: '0.1',
                    markup: '13',
                    commissionPercentage: '5'
                };

                break;
            case 'hsaBalances':
                exampleData = {
                    email: 'dude@simply.ca',
                    balance: '35000',
                    maximum: '4000'
                };

                break;
            case 'lsaBalances':
                exampleData = {
                    email: 'dude@simply.ca',
                    balance: '35000',
                    maximum: '4000'
                };

                break;
            case 'adjustments':
                exampleData = {
                    email: 'dude@simply.ca',
                    benefit: 'life',
                    net: '-100.010',
                    RST: '-12.000',
                    PST: 0,
                    GST: '0',
                    HST: '0',
                    total: '112.010',
                    isProtected: 'yes',
                    referencingInvoiceNum: 123456,
                    note: 'This is happening cause....'
                };

                break;
            case 'dentalPayees':
                exampleData = {
                    'Office Num': '156',
                    'Address': '123 Fake Street',
                    'Province': 'BC',
                    'Phone': '250-456-7890',
                    'Postal Code': 'V1Y 4H5',
                    'First Name': 'John',
                    'Last Name': 'Smith',
                    'CDA ID': '123456',
                    'CDANet UIN': '78910'
                };

                break;

            case 'grandfatherClaims':
                exampleData = {
                    "Group Number": '156',
                    "First Name": 'John',
                    "Last Name": 'Smith',
                    "Birthdate": '1991-08-15',
                    "Relationship": 'Self/Spouse/Child/Common Law',
                    "Benefit": 'Critical Illness/Dependent Life/Long Term Disability/Short Term Disability/Life Insurance/Drugs/Dental/Health/Paramedicals/Vision/Out Of Country/Employee Assistance/Cost Plus',
                    "Service": 'Critical Illness/Dependent Life/Long Term Disability/Short Term Disability/Life Insurance/Erectile Dysfunction Drugs/Fertility Drugs/Generic Drugs/Routine Care/Periodontics/Endodontics/Denture Repair/Dentures/Bridge Work/Crowns/Orthodontics/Private Duty Nursing/Hospital Benefits/Ambulance Services/Orthotics/Orthopedic Shoes/Prosthetics/Hearing Aids/Medical Services & Supplies/Medical Equipment/Cardiac Rehabilitation/Convalescent Hospital/Chiropractor/Massage/Naturopath/Osteopath/Physiotherapy/Podiatrist/Psychologist/Speech Therapy/Social Worker/Acupuncturist/Chiropodist/Audiologist/Registered Dietician/Eye Exam/Eye Glasses & Contacts/Required Safety Eyewear/Out Of Province/Out Of Province Medical Referral/Employee Assistance/Medical Second Opinion/Eldercare/Occupational Therapist/Routine Care Visit/Cost Plus/Cataract Surgery Eyewear/Preventative Vaccines/Smoking Cessations/Diagnostic Services/Basic Oral Surgery/Major Oral Surgery/Apnea Machine (CPAP)/Apnea Machine Supplies/Apnea Mask/Artificial Eye/Limb/Artificial Eye/Limb Repair/Replacement/Blood Pressure Monitor/Braces With Rigid Supports/Compression Stockings/Crutches/Glucose Monitoring Equipment and Supplies/Insulin Pump/IPP Breathing Machine/Ostomy Supplies/Surgical Bra/TENS Machine/Viscosupplementation/Wheelchair - Manual/Wheelchair - Electric/Wigs/Accidental Dental/Medical Referral/Insulin Pump Supplies',
                    "Amount Submitted": '50.25',
                    "Adjudicated Amount": '30.25',
                    "Amount Paid": '30.25',
                    "Deductible Paid": "0",
                    "Service Date": '2024-05-01',
                    "Date Paid": '2024-05-02',
                    "Carrier": 'Allstate',
                    "Province": 'British Columbia',
                    "Procedure Number": "1101",
                    "ASO": '0'
                };

                break;
            case 'claims':
                exampleData = {
                    cert: 123456,
                    category: 'Paramedicals',
                    service: 'Massage',
                    amountSubmitted: 150.00,
                    adjudicatedAmount: 80.00,
                    amountPaid: 80.00,
                    deductiblePaid: 0,
                    carrier: 'Allstate',
                    serviceDate: '2024-01-20'
                };

                break;
            default:
                exampleData = false;
                break;
        }

        if (exampleData) {
            const data = [ exampleData ];

            // create a csv based off the required fields
            const options = {
                fieldSeparator: ',',
                filename: `${ this.state.uploadType } template`,
                quoteStrings: '"',
                decimalSeparator: '.',
                showLabels: true,
                showTitle: false,
                useTextFile: false,
                useBom: true,
                useKeysAsHeaders: true
            };
            const csvExporter = new ExportToCsv(options);

            csvExporter.generateCsv(data);
        }
    };

    render() {

        let tiles = [
            {
                title: 'Dental Fees',
                description: 'Upload Dental Fees.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'dentalFees');
                }
            },
            {
                title: 'Dental Payees',
                description: 'Upload Dental Payee Data.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'dentalPayees');
                }
            },
            {
                title: 'Optional Benefits',
                description: 'Upload Optional Benefits.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'optionalBenefits');
                }
            },
            {
                title: 'Misc Premiums',
                description: 'Upload Misc Premiums.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'miscPremiums');
                }
            },
            {
                title: 'Invoice Adjustments',
                description: 'Upload Member Adjustments.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'adjustments');
                }
            },
            {
                title: 'Grandfathered Claims',
                description: 'Grandfather claims from a previous carrier.',
                icon: 'Uploads',
                onClick: () => {
                    this._toggleModal('open', 'grandfatherClaims');
                }
            }
        ];

        return (
            <ContainerControlSite>
                <Header
                    subHeader="Uploads"
                    selectedTitle="reports"
                />

                <Content>
                    {
                        this.state.failedRecords &&
                            <div style={{ marginBottom: 20 }}>
                                <Text bold color="red" margin="0 0 10px 0">
                                    The data file upload failed due to the below file errors. Please correct the records and re-upload the file.
                                </Text>

                                {
                                    this.state.failedRecords.map((record) => (
                                        <div style={{ marginTop: 10 }}>
                                            <Text color="red">
                                                { `Record for ${ record.label } failed for the following reason(s): ` }
                                            </Text>

                                            {
                                                record.failureVerbiages.map((verbiage) => <Text small margin="0 0 0 20px">&#x2022;&nbsp;&nbsp;{ verbiage }</Text>)
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                    }

                    <Tiles tiles={tiles} />
                </Content>

                {this.props.loader?.isLoading && <Loader isContainer/>}

                {
                    this.state.modalOpen &&
                        <FileDropZoneModal
                            title="Please select files"
                            hideModal={this._toggleModal}
                            showModal={this.state.modalOpen}
                            maxItems={1}
                            type={this.state.uploadType}
                            submitFile={this._submitFile}
                            instructions={this.state.instructions}
                            warning={this.state.warning}
                            acceptedFiles=".csv"
                            downloadTemplate={this._downloadTemplate}
                        />
                }

                <span id="uploads" />
            </ContainerControlSite>
        );
    }
}

Uploads.propTypes = {
    accessType: PropTypes.string
};

/**
 * @param state
 * @returns {{loading: *}}
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    accessType: state.master.access
});

export default (connect(mapStateToProps)(Uploads));